<template>
  <div
    class="search-bar"
    :class="{ error: error }"
    @mousedown="handleMouseDown"
  >
    <span v-if="$slots.default">
      <slot></slot>
    </span>
    <div
      :class="{
        'no-bottom-border': searchResults.length > 0 && focused,
      }"
    >
      <input
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :required="required"
        :data-test="dataTest || null"
        :class="{ 'error-input': error }"
        :aria-label="errorMessage"
        autocomplete="off"
        data-1p-ignore
        @input="$emit('update:modelValue', $event.target.value)"
        @focus="focused = true"
        @blur="handleBlur"
        @keydown.down.prevent="downButtonPressed"
        @keydown.up.prevent="upButtonPressed"
        @keydown.enter="enterButtonPressed"
      />

      <SearchIcon />
      <span
        v-if="error"
        class="error-message"
      >
        {{ fullErrorMessage }}
      </span>
      <transition
        name="fade"
        mode="out-in"
      >
        <ul v-if="searchResults.length > 0 && focused">
          <li
            v-for="(result, i) in searchResults"
            :key="result.id"
            :class="{ active: i === active }"
            :data-test="`option-${i + 1}`"
            @mousedown.prevent
            @click="handleResultClick(result)"
            @mouseover="active = i"
            @mouseleave="active = null"
          >
            {{ result.name }}
          </li>
        </ul>
      </transition>

      <span
        v-if="showRightIcon"
        class="icon-right"
      >
        <SpinnerIcon v-if="resultsLoading" />

        <AlertCircleIcon
          v-else-if="error"
          class="error-icon"
        />
      </span>
    </div>
  </div>
</template>

<script>
import AlertCircleIcon from '~/components/Common/Svg/AlertCircleIcon.vue';
import SearchIcon from '~/components/Common/Svg/SearchIcon';
import SpinnerIcon from '~/components/Common/Svg/SpinnerIcon.vue';

export default {
  components: {
    SearchIcon,
    SpinnerIcon,
    AlertCircleIcon,
  },
  props: {
    errorMessage: {
      type: String,
      default: '',
    },
    customErrorMessage: {
      type: String,
      default: '',
    },
    resultsLoading: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    searchResults: {
      type: Array,
      default: () => [],
    },
    error: {
      type: Boolean,
      default: false,
    },
    dataTest: {
      type: String,
      default: '',
    },
    preventDefaultOnEnter: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:modelValue', 'selected'],
  data() {
    return {
      active: null,
      focused: false,
    };
  },
  computed: {
    showRightIcon() {
      return this.resultsLoading || this.error;
    },
    fullErrorMessage() {
      if (this.customErrorMessage !== '') {
        return this.customErrorMessage;
      }

      return `Please enter a valid ${this.errorMessage || this.placeholder}`;
    },
  },
  watch: {
    value() {
      this.active = null;
    },
  },
  methods: {
    downButtonPressed() {
      if (this.active === this.searchResults.length - 1) {
        return;
      }
      if (this.active === null) {
        this.active = 0;
      } else {
        this.active += 1;
      }
    },
    upButtonPressed() {
      if (this.active === null) {
        return;
      }
      if (this.active === 0) {
        this.active = 0;
      } else {
        this.active -= 1;
      }
    },
    enterButtonPressed(event) {
      if (this.preventDefaultOnEnter) {
        event.preventDefault();
      }
      if (this.active !== null) {
        this.$emit('selected', this.searchResults[this.active]);
        this.active = null;
        this.focused = false;
      }
    },
    handleResultClick(result) {
      this.$emit('selected', result);
      this.active = null;
    },
    handleMouseDown(event) {
      if (!this.$el.contains(event.target)) {
        this.focused = false;
      }
    },
    handleBlur() {
      this.$nextTick(() => {
        if (!this.$el.contains(document.activeElement)) {
          this.focused = false;
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './SearchBar.scss';
</style>
